import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1762337009787-bad651efb880?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMG5pZ2h0fGVufDB8fHx8MTc4NzQ5NjQ3MHww&ixlib=rb-4.1.0&q=85";

const lines = ["WHERE DREAMS", "BECOME REALITY"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-screen min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Luxury architecture at night" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24 lg:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-accent text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-6"
          data-testid="hero-eyebrow"
        >
          Taha Developers — Dahod, Gujarat
        </motion.p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.95]" data-testid="hero-heading">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "gold-text italic" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.4 + i * 0.18, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9 }}
          className="mt-8 max-w-xl text-zinc-400 text-base leading-relaxed"
          data-testid="hero-subtext"
        >
          Crafting landmark residences and retail destinations in Dahod — where every brick is laid with intent, and every address becomes a legacy.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href="#projects"
            data-testid="hero-view-projects-button"
            className="bg-[#C5A059] text-black font-accent text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-[#D4AF37] hover:-translate-y-1 transition-all duration-300"
          >
            View Projects
          </a>
          <a
            href="#contact"
            data-testid="hero-contact-button"
            className="border border-white/25 text-white font-accent text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] hover:-translate-y-1 transition-all duration-300"
          >
            Speak With Us
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-8 lg:right-12 z-10 hidden sm:flex items-center gap-3 font-accent text-[10px] tracking-[0.3em] uppercase text-zinc-500"
        data-testid="hero-scroll-hint"
      >
        <span>Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="block w-px h-10 bg-gradient-to-b from-[#C5A059] to-transparent" />
      </motion.div>
    </section>
  );
}
